/**
 * Settled fixtures enable repeatable layout review without calendar sync or two LLM pipelines. The gallery
 * route in dev/gallery.py is restricted to allowed tooling environments.
 */
import type { AssessmentState, CalendarContext, CalendarScanStats, IdentityPrefill, OnboardingReading, ProfilePreview, ReadingsState } from "~vue/CalendarFirstOnboarding/types";
import type { PerspectiveResult } from "~vue/pages/perspectiveTypes";
import type { SetupStep } from "~vue/ProfileSetup/types";

export const ORGANIZATION_NAME = "Northwind";

export const IDENTITY_PREFILL: IdentityPrefill = {
    first_name: "Chris",
    last_name: "Honiball",
    role: "Director of Engineering",
    source: "Workday",
};

export const CALENDAR: CalendarContext = {
    provider: "google",
    provider_label: "Google Calendar",
    connected: false,
    auth_url_endpoint: "/calendar/auth-url/",
    timezone: "America/Chicago",
    timezones: ["America/Chicago", "America/Los_Angeles", "America/New_York", "Europe/Dublin", "Europe/London", "UTC"],
};

export const SCAN_STATS: CalendarScanStats = {
    window_days: 30,
    provider_name: "Google Calendar",
    meeting_count: 96,
    total_meeting_hours: 128,
    hours_per_week: 32,
    organized_count: 34,
    attended_count: 62,
    top_people: [
        { name: "Priya Raman", meeting_count: 21, shared_hours: 17.5, has_visible_profile: true },
        { name: "Tom Okafor", meeting_count: 16, shared_hours: 12, has_visible_profile: true },
        { name: "Dana Whitfield", meeting_count: 12, shared_hours: 9.5, has_visible_profile: false },
        { name: "Sam Alvarez", meeting_count: 9, shared_hours: 6, has_visible_profile: false },
    ],
    profile_sharer_names: ["Priya Raman", "Tom Okafor", "Dana Whitfield"],
    recurring_series_count: 11,
    recurring_occurrence_count: 58,
    recurring_hours: 74,
    busiest_weekday: "Tuesday",
    back_to_back_count: 23,
    sparse: false,
};

let readingSeq = 0;

function reading(category: string, title: string, summary: string, body: string, labels: [string, string][], unknowns: string[]): OnboardingReading {
    readingSeq += 1;
    return {
        id: `00000000-0000-0000-0000-${String(readingSeq).padStart(12, "0")}`,
        // Tier 1 on the My Work scale, which is what a calendar-only reading is.
        experiment_slug: "calendar_insights",
        category,
        title,
        summary,
        body,
        unknowns,
        evidence_groups: [
            {
                kind: "calendar_event",
                label: "Calendar",
                count: labels.length,
                items: labels.map(([label, detail]) => ({ label, detail })),
            },
        ],
    };
}

export const FIRST_PASS_READINGS: ReadingsState = {
    status: "ready",
    sparse: false,
    readings: [
        reading(
            "time",
            "Your Tuesdays are not yours",
            "11 of your 32 weekly meeting hours land on one day.",
            "Tuesday carries your staff meeting, both skip-levels and the platform review — 11 hours, most of it back to back. The other four days average five. Whatever needs your full attention is competing with a day that has none left.",
            [
                ["Weekly staff", "9:00–10:30, every Tuesday"],
                ["Platform review", "13:00–14:30, 4 of the last 4 weeks"],
            ],
            ["Whether the Tuesday load is deliberate"],
        ),
        reading(
            "people",
            "Priya is your default second opinion",
            "You spend more one-to-one time with Priya than with your next two reports combined.",
            "21 meetings in 30 days, 17.5 hours, and 8 of them booked with under a day's notice. That pattern usually means one of two things: she is the person you think with, or she is the escalation path for work that should not need one.",
            [
                ["1:1 Priya / Chris", "21 meetings, 17.5 hours"],
                ["Ad-hoc syncs", "8 booked inside 24 hours"],
            ],
            ["Whether the short-notice syncs are yours or hers"],
        ),
        reading(
            "focus",
            "You organize a third of what you attend",
            "34 of 96 meetings are yours; the rest arrive.",
            "That ratio is normal for a director, but the shape is not: your own meetings cluster in the morning and everything you were invited to lands after 2pm, which is where all 23 back-to-back runs start.",
            [["Organizer split", "34 organized, 62 attended"]],
            ["What the afternoon invitations are for"],
        ),
    ],
};

export const SHARPENED_READINGS: ReadingsState = {
    status: "ready",
    sparse: false,
    readings: [
        reading(
            "time",
            "The Tuesday load is costing you more than it costs most people",
            "You recharge alone, and Tuesday gives you no gap to do it in.",
            "Your answers put you well toward the reflective end — you think before you speak and you settle decisions on your own. 11 back-to-back hours does not just tire you out, it removes the step you actually decide in. The fix is not fewer meetings, it is thirty minutes between them.",
            [
                ["Weekly staff", "9:00–10:30, every Tuesday"],
                ["Active / Reflective", "Reflective, strongly"],
            ],
            ["Whether you have tried protecting the gap before"],
        ),
        reading(
            "people",
            "Priya gets your thinking; the rest of your team gets your conclusions",
            "The person you process with is getting five times the airtime of anyone else.",
            "You lean contained rather than expressive, so the thinking happens once, with Priya, and everyone else meets the decision. That is efficient and it is also why your skip-levels keep surfacing things you thought were settled.",
            [
                ["1:1 Priya / Chris", "21 meetings, 17.5 hours"],
                ["Expressive / Contained", "Contained"],
            ],
            ["Whether the rest of the team knows they can push back"],
        ),
        reading(
            "focus",
            "Your mornings are designed and your afternoons are not",
            "You plan your mornings and inherit your afternoons.",
            "You score planful rather than open-ended, which is why the morning block reads as designed and the afternoon reads as weather. 23 back-to-back runs, nearly all of them after 2pm, are the part of the week nobody is designing — including you.",
            [["Back-to-back runs", "23 in 30 days, 19 after 14:00"]],
            ["Who owns the afternoon invitations"],
        ),
    ],
};

export const PERSPECTIVE_RESULT: PerspectiveResult = {
    letters: "INTJ",
    archetype: "Architect",
    dimensions: [
        {
            code: "EI",
            raw_score: 1.8,
            binned_score: 2,
            lenses: [
                { lens: "initiating-receiving", raw_score: 1.4, binned_score: 2, binned_label: "Receiving" },
                { lens: "expressive-contained", raw_score: 2.1, binned_score: 3, binned_label: "Contained" },
                { lens: "gregarious-intimate", raw_score: 1.2, binned_score: 1, binned_label: "Intimate" },
                { lens: "active-reflective", raw_score: 2.4, binned_score: 3, binned_label: "Reflective" },
                { lens: "enthusiastic-quiet", raw_score: 0.8, binned_score: 1, binned_label: "Quiet" },
            ],
        },
        {
            code: "SN",
            raw_score: 1.6,
            binned_score: 2,
            lenses: [
                { lens: "concrete-abstract", raw_score: 1.5, binned_score: 2, binned_label: "Abstract" },
                { lens: "realistic-imaginative", raw_score: 1.1, binned_score: 1, binned_label: "Imaginative" },
                { lens: "practical-conceptual", raw_score: 2.0, binned_score: 2, binned_label: "Conceptual" },
                { lens: "experiential-theoretical", raw_score: 1.8, binned_score: 2, binned_label: "Theoretical" },
                { lens: "traditional-original", raw_score: 1.4, binned_score: 1, binned_label: "Original" },
            ],
        },
        {
            code: "TF",
            raw_score: -1.2,
            binned_score: -1,
            lenses: [
                { lens: "logical-empathetic", raw_score: -1.9, binned_score: -2, binned_label: "Logical" },
                { lens: "reasonable-compassionate", raw_score: -0.6, binned_score: -1, binned_label: "Reasonable" },
                { lens: "questioning-accommodating", raw_score: -1.4, binned_score: -1, binned_label: "Questioning" },
                { lens: "critical-accepting", raw_score: -0.9, binned_score: -1, binned_label: "Critical" },
                { lens: "tough-tender", raw_score: -0.4, binned_score: 0, binned_label: "Balanced" },
            ],
        },
        {
            code: "JP",
            raw_score: -1.9,
            binned_score: -2,
            lenses: [
                { lens: "systematic-casual", raw_score: -2.2, binned_score: -3, binned_label: "Systematic" },
                { lens: "planful-openended", raw_score: -1.7, binned_score: -2, binned_label: "Planful" },
                { lens: "earlystarting-pressureprompted", raw_score: -1.3, binned_score: -1, binned_label: "Early starting" },
                { lens: "scheduled-spontaneous", raw_score: -2.0, binned_score: -2, binned_label: "Scheduled" },
                { lens: "methodical-emergent", raw_score: -1.5, binned_score: -2, binned_label: "Methodical" },
            ],
        },
    ],
};

/*
 * Production serves 60 Perspectives items; these 20 fixtures exercise gallery pagination, progress, and
 * footer layout.
 */
const ASSESSMENT_PROMPTS: [string, string][] = [
    ["I say what I think as I think it, rather than after I have worked it out.", "expressive-contained"],
    ["I would rather start a conversation than wait to be brought into one.", "initiating-receiving"],
    ["A big group energises me more than one long conversation does.", "gregarious-intimate"],
    ["I work things out by talking them through, not by sitting with them.", "active-reflective"],
    ["People would call me visibly enthusiastic in a meeting.", "enthusiastic-quiet"],
    ["I trust what I can measure over what I can picture.", "concrete-abstract"],
    ["I would rather improve what exists than imagine what could.", "realistic-imaginative"],
    ["Give me the practical version before the framework behind it.", "practical-conceptual"],
    ["I learn a thing properly by doing it, not by studying it.", "experiential-theoretical"],
    ["A proven way of working beats an original one.", "traditional-original"],
    ["I decide on the logic and let the feelings catch up.", "logical-empathetic"],
    ["Being fair matters more to me than being kind.", "reasonable-compassionate"],
    ["My instinct with a new proposal is to look for the hole in it.", "questioning-accommodating"],
    ["I would rather name the problem than let it pass.", "critical-accepting"],
    ["I can deliver hard news without softening it.", "tough-tender"],
    ["I keep my work in an order someone else could follow.", "systematic-casual"],
    ["I want the plan settled before we start.", "planful-openended"],
    ["I start well before the deadline, not just before it.", "earlystarting-pressureprompted"],
    ["A scheduled day suits me better than an open one.", "scheduled-spontaneous"],
    ["I follow a method rather than finding the shape as I go.", "methodical-emergent"],
];

export const ASSESSMENT: AssessmentState = {
    session_id: "00000000-0000-0000-0000-0000000000aa",
    questions: ASSESSMENT_PROMPTS.map(([text, lens], index) => ({
        id: `00000000-0000-0000-0000-${String(index).padStart(12, "0")}`,
        text,
        dimension: ["EI", "SN", "TF", "JP"][Math.floor(index / 5)],
        lens,
        direction: index % 2 === 0 ? 1 : -1,
        display_order: index,
    })),
    saved_responses: {},
};

export const PROFILE_PREVIEW: ProfilePreview = {
    status: "ready",
    name: "Chris Honiball",
    role: "Director of Engineering",
    had_existing_content: false,
    subtopics: [
        {
            id: "00000000-0000-0000-0000-000000000001",
            title: "Communication Style",
            placeholder: "",
            content:
                "I think before I speak. If you bring me something in a meeting and I go quiet, that is me working, not stalling — come back the next day and you will get a real answer rather than a fast one. Written context ahead of a decision is worth more to me than an extra half hour in the room.",
        },
        {
            id: "00000000-0000-0000-0000-000000000002",
            title: "Work Structure",
            placeholder: "",
            content:
                "Lead with the decision you want and what you have already ruled out. I will push on the reasoning, sometimes hard; that is interest, not disapproval. If I have not pushed back at all, I probably have not read it properly yet — chase me.",
        },
        {
            id: "00000000-0000-0000-0000-000000000003",
            title: "Motivators & Stressors",
            placeholder: "",
            content:
                "Spreading the thinking wider than one person. Too much of how I reach a decision happens in a single 1:1, which means the rest of the team keeps meeting conclusions instead of arguments. I am trying to do more of that reasoning in the open, even when it is slower.",
        },
        {
            id: "00000000-0000-0000-0000-000000000004",
            title: "Feedback & Collaboration",
            placeholder: "",
            content:
                "Tell me when a decision landed on you without the reasoning behind it. That is the failure mode I cannot see from where I sit, and it is the one I would most like to fix this year.",
        },
    ],
};

export const SETUP_STEPS_MIXED: SetupStep[] = [
    { id: "identity", label: "About you", action: "Add details", done_action: "Edit", done: true },
    { id: "calendar", label: "Calendar", action: "Connect calendar", done_action: "Connected", done: true },
    { id: "perspective", label: "Perspective", action: "Take assessment", done_action: "Retake", done: false },
    { id: "publish", label: "Collaboration profile", action: "Review & publish", done_action: "Published", done: false },
];
